import React, { useCallback, useEffect, useState } from 'react';
import { engagementApi, leadApi, marketingApi } from '../services/api';
import { BySource, LeadRow, MetaCampaignSpend, MetaCampaignSpendResponse, SourceFunnel } from '../types';
import { fmtDayTime, fmtINR, istToday } from '../lib';
import DateRangeBar from '../components/DateRangeBar';
import NumberStrip, { StripItem } from '../components/NumberStrip';
import StageChips, { Dash } from '../components/StageChips';
import {
  Section, Empty, Failed, Loading, Note, PageFrame, picked, Pill, PlatformLeadsCard, Strip, Th, VisitsCell,
  btn, btnTone, td, tdBig, tdOurs, trClick,
} from '../components/PlatformBits';

/** One Meta campaign's row: what Meta delivered (spend side) next to what
 *  the CRM holds for the leads it brought (outcome side). Outcomes are the
 *  first-touch cohort: leads whose first touch was one of the campaign's ads
 *  (`a:<ad_id>`) or a site visit from its link (`utm:…/<campaign_id>`). */
interface CampaignRow {
  key: string;
  name: string;
  objective: string | null;
  status: string | null;
  spend: number | null;        // null = no spend synced for this window
  impressions: number;
  link_clicks: number;
  conversations: number;
  tags: string[];              // the first-touch tags rolled into this row
  new_leads: number;
  stages: Record<string, number>;
  visits_scheduled: number;
  visits_conducted: number;
}

const OTHER_KEY = 'other-meta-ads';

const rollUp = (spend: MetaCampaignSpend[], sources: SourceFunnel[]): CampaignRow[] => {
  const rows = new Map<string, CampaignRow>();
  const byAd = new Map<string, string>();
  spend.forEach((c) => {
    c.ads.forEach((ad) => byAd.set(ad, c.campaign_id));
    rows.set(c.campaign_id, {
      key: c.campaign_id, name: c.campaign_name, objective: c.objective, status: c.status,
      spend: c.spend, impressions: c.impressions, link_clicks: c.link_clicks, conversations: c.conversations,
      tags: [], new_leads: 0, stages: {}, visits_scheduled: 0, visits_conducted: 0,
    });
  });
  sources.forEach((s) => {
    if (!s.tag) return;
    // A CTWA first touch names its ad; a site arrival names the campaign.
    const key = s.source_kind === 'ad'
      ? (byAd.get(s.content ?? '') ?? OTHER_KEY)
      : (s.campaign && rows.has(s.campaign) ? s.campaign : null);
    if (!key) return;
    if (!rows.has(key)) {
      rows.set(key, {
        key, name: 'Other Meta ads', objective: null, status: null,
        spend: null, impressions: 0, link_clicks: 0, conversations: 0,
        tags: [], new_leads: 0, stages: {}, visits_scheduled: 0, visits_conducted: 0,
      });
    }
    const r = rows.get(key)!;
    r.tags.push(s.tag);
    r.new_leads += s.new_leads;
    r.visits_scheduled += s.visits_scheduled;
    r.visits_conducted += s.visits_conducted;
    Object.entries(s.stages).forEach(([st, n]) => { r.stages[st] = (r.stages[st] ?? 0) + n; });
  });
  return Array.from(rows.values()).sort((a, b) => (b.spend ?? -1) - (a.spend ?? -1));
};

const objectiveLabel = (o: string | null): string | null =>
  o ? o.replace(/^OUTCOME_/, '').replace(/_/g, ' ').toLowerCase() : null;

/** Meta's delivery status as the one pill language: running green, paused
 *  quiet, anything else (disapproved, with issues…) a warning. */
const StatusPill: React.FC<{ status: string | null }> = ({ status }) => {
  if (!status) return null;
  if (status === 'ACTIVE') return <Pill tone="ok">active</Pill>;
  if (/PAUSED/.test(status)) return <Pill>paused</Pill>;
  return <Pill tone="warn">{status.replace(/_/g, ' ').toLowerCase()}</Pill>;
};

/** The selected row's first cell carries a forest rule on its left edge. */

const MetaPage: React.FC = () => {
  const [range, setRange] = useState({ from: istToday(), to: istToday() });
  const [spend, setSpend] = useState<MetaCampaignSpendResponse | null>(null);
  const [data, setData] = useState<BySource | null>(null);
  const [leads, setLeads] = useState<LeadRow[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setSpend(null); setData(null); setLeads(null); setError(null);
    Promise.all([
      marketingApi.campaignSpend(range.from, range.to),
      engagementApi.bySource(range.from, range.to),
    ]).then(([sp, bs]) => {
      setSpend(sp); setData(bs);
      const tags = rollUp(sp.campaigns, bs.sources).flatMap((r) => r.tags);
      return leadApi.byFirstTouch(tags, range.from, range.to).then(setLeads);
    }).catch((err: any) => setError(err?.response?.data?.detail || err.message));
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => {
    setRefreshing(true);
    marketingApi.refreshInsights()
      .then(load)
      .catch((err: any) => setError(`Refresh failed: ${err?.response?.data?.detail || err.message}`))
      .finally(() => setRefreshing(false));
  };

  if (error) return <Failed what="Meta" error={error} />;

  const rows = spend && data ? rollUp(spend.campaigns, data.sources) : [];
  if (selected && !rows.some((r) => r.key === selected)) setSelected(null);
  const shown = selected ? rows.filter((r) => r.key === selected) : rows;
  const selectedRow = selected ? shown[0] : undefined;
  const tot = shown.reduce((a, r) => ({
    spend: a.spend + (r.spend ?? 0), leads: a.leads + r.new_leads,
    sched: a.sched + r.visits_scheduled, done: a.done + r.visits_conducted,
    intent: a.intent + (r.stages.visit_intent ?? 0),
  }), { spend: 0, leads: 0, sched: 0, done: 0, intent: 0 });

  const strip: StripItem[] = [
    { v: tot.done, k: 'site visits', cls: 'visited' },
    { v: tot.sched, k: 'scheduled', cls: 'gold' },
    { v: tot.intent, k: 'visit intent' },
    { v: tot.leads, k: 'new leads' },
    { v: fmtINR(tot.spend), k: 'spend' },
    ...(tot.leads > 0 ? [{ v: fmtINR(tot.spend / tot.leads), k: 'per lead' }] : []),
  ];

  const selectedTags = new Set(selected ? shown.flatMap((r) => r.tags) : []);
  const leadRows = (leads ?? []).filter((l) => !selected || selectedTags.has(l.acquisition?.src ?? ''));
  const tagToCampaign = new Map(rows.flatMap((r) => r.tags.map((t) => [t, r.name] as const)));

  return (
    <PageFrame
      title="Meta"
      badge={{ platform: 'meta' }}
      subline={<>Bhuvana Infra ad account · {spend?.synced_at ? <>synced <b className="text-charcoal">{fmtDayTime(spend.synced_at)}</b></> : 'sync time unknown'}</>}
      action={<button onClick={refresh} disabled={refreshing || !spend} className={`${btn} ${btnTone.forest}`}>{refreshing ? 'Refreshing…' : 'Refresh from Meta'}</button>}
      bar={<DateRangeBar from={range.from} to={range.to} onChange={(from, to) => setRange({ from, to })} />}
    >
      {!(spend && data) && <Loading what="Meta" />}

      {spend && data && (
        <>
          <Strip><NumberStrip items={strip} /></Strip>

          <Section title="Campaigns" note={selectedRow
            ? <>leads below are from <b className="text-charcoal">{selectedRow.name}</b> · <button className="font-semibold text-forest hover:underline" onClick={() => setSelected(null)}>show all</button></>
            : 'click a campaign to see its leads'}>
            <table className="w-full min-w-[980px] border-collapse">
              <thead><tr>
                <Th left>Campaign</Th><Th>Spend</Th><Th>Clicks</Th><Th>Chats</Th>
                <Th ours>New leads</Th><Th ours>Stage</Th><Th ours>Site visits</Th><Th>₹ / lead</Th><Th>₹ / visit</Th>
              </tr></thead>
              <tbody>
                {rows.length === 0 && <Empty cols={9}>No Meta spend or Meta leads in this window.</Empty>}
                {rows.map((r) => (
                  <tr key={r.key} onClick={() => setSelected(selected === r.key ? null : r.key)} className={trClick(selected === r.key)}>
                    <td className={`px-3 py-3 ${picked(selected === r.key)}`}>
                      <div className="font-semibold text-[13.5px] leading-snug truncate max-w-[340px]" title={r.name}>{r.name}</div>
                      {(r.status || r.objective) && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <StatusPill status={r.status} />
                          {objectiveLabel(r.objective) && <Pill>{objectiveLabel(r.objective)}</Pill>}
                        </div>
                      )}
                    </td>
                    <td className={tdBig}>
                      {r.spend != null ? fmtINR(r.spend) : <Dash />}
                      {r.impressions > 0 && <div className="text-[11px] text-graphite font-normal">{r.impressions.toLocaleString('en-IN')} impressions</div>}
                    </td>
                    <td className={`${td} text-right`}>
                      {r.link_clicks ? <>{r.link_clicks.toLocaleString('en-IN')}{r.spend != null && <div className="text-[11px] text-graphite">{fmtINR(r.spend / r.link_clicks)} each</div>}</> : <Dash />}
                    </td>
                    <td className={`${td} text-right`}>{r.conversations || <Dash />}</td>
                    <td className={tdOurs}>{r.new_leads || <Dash />}</td>
                    <td className="px-3 py-3 text-right">{r.new_leads ? <StageChips stages={r.stages} /> : <Dash />}</td>
                    <VisitsCell done={r.visits_conducted} sched={r.visits_scheduled} />
                    <td className={`${td} text-right`}>{r.spend != null && r.new_leads ? fmtINR(r.spend / r.new_leads) : <Dash />}</td>
                    <td className={`${td} text-right`}>{r.spend != null && r.visits_conducted ? fmtINR(r.spend / r.visits_conducted) : <Dash />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Note>
              Grey columns are Meta's, from the nightly sync. Green columns are leads whose first touch was one of the campaign's ads; their visits count whenever they fall.
              {' '}<b className="text-charcoal">Other Meta ads</b> — leads from an ad with no spend in this window.
            </Note>
          </Section>

          <PlatformLeadsCard
            title={selectedRow ? `Leads from ${selectedRow.name}` : 'Leads from Meta'} leads={leads} rows={leadRows} empty="No lead in this window came from a Meta ad."
            origin={selected ? undefined : { header: 'Campaign', of: (l) => { const c = tagToCampaign.get(l.acquisition?.src ?? '') ?? 'Other Meta ads'; return <span title={c}>{c}</span>; } }}
          />
        </>
      )}
    </PageFrame>
  );
};

export default MetaPage;
